import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ShieldCheck,
  Sparkles,
  BarChart3,
  Layers,
  Zap,
  Users,
} from "lucide-react";
import { MODULES, AI_MODULE } from "@/lib/modules";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_compliance-platform-12/artifacts/0nh5z3dw_Nurse%201.jpg";

const PROOF_POINTS = [
  { value: "100+", label: "Hospitals since 2015" },
  { value: "60%", label: "Less time capturing assurance data" },
  { value: "30%", label: "Reduction in clinical audit resource" },
  { value: "40%", label: "Reduction in compliance workload" },
];

const STANDARDS = [
  "NSQHS",
  "Aged Care Quality Standards",
  "HDSS",
  "JCI",
  "CQC",
  "Accreditation Canada",
  "HAS",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white" data-testid="hero-section">
        <div className="absolute inset-0 bg-grid opacity-70 pointer-events-none" />
        <div className="absolute -top-40 -right-32 w-[500px] h-[500px] rounded-full bg-[#22A34A]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full bg-[#3FB8E5]/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#22A34A]/10 text-[#22A34A] text-xs font-bold tracking-[0.18em] uppercase mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#22A34A]" />
                Clinical Governance, Simplified
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                One platform for{" "}
                <span className="text-[#22A34A]">complete clinical governance.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
                QC4Health is the integrated, AI-powered platform that simplifies
                clinical governance for healthcare organisations. Reduce risk,
                demonstrate compliance with confidence, and deliver better
                patient outcomes — trusted by over 100 hospitals since 2015.
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/book-demo"
                  data-testid="hero-book-demo-btn"
                  className="inline-flex items-center gap-2 bg-[#22A34A] hover:bg-[#1e9042] text-white rounded-full px-8 py-3.5 font-medium transition-all hover:-translate-y-0.5 shadow-[0_10px_28px_-8px_rgba(34,163,74,0.55)]"
                >
                  Book a Demo
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/product"
                  data-testid="hero-explore-product-btn"
                  className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-300 text-slate-900 rounded-full px-8 py-3.5 font-medium transition-all hover:-translate-y-0.5"
                >
                  Explore the platform
                </Link>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-500">
                <span className="inline-flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#22A34A]" />
                  AI-supported, out-of-the-box
                </span>
                <span className="inline-flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#22A34A]" />
                  Fully integrated
                </span>
                <span className="inline-flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#22A34A]" />
                  Cloud, secure, accreditation-ready
                </span>
              </div>
            </div>

            <div className="lg:col-span-5 fade-up fade-up-delay-2">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-200">
                <img
                  src={HERO_IMG}
                  alt="Clinician using QC4Health"
                  className="w-full h-[500px] object-cover object-[30%_center]"
                />
                <div className="absolute inset-x-6 bottom-6 bg-white rounded-2xl p-5 shadow-xl border border-slate-100">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-xl bg-[#22A34A]/10 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-[#22A34A]" />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-500">
                        Live Assurance
                      </div>
                      <div className="text-sm font-semibold text-slate-900">
                        Population-based monitoring
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <div className="text-3xl font-heading font-semibold text-slate-900">
                      98.4%
                    </div>
                    <div className="text-xs text-[#22A34A] font-medium">
                      compliance across 42 wards
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROOF POINTS */}
      <section className="border-y border-slate-200 bg-white" data-testid="proof-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
            {PROOF_POINTS.map((p, i) => (
              <div key={i} className="text-center md:text-left" data-testid={`proof-point-${i}`}>
                <div className="font-heading text-4xl md:text-5xl font-semibold text-slate-900 tracking-tight">
                  {p.value}
                </div>
                <div className="mt-2 text-sm text-slate-500 leading-snug">
                  {p.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE CHALLENGE */}
      <section className="py-20 md:py-28" data-testid="challenge-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#22A34A] mb-4">
              The Challenge
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight leading-tight">
              Clinical governance is harder than it should be.
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Policies, incidents, audits and credentials live in separate
              spreadsheets and tools. Teams chase paperwork and evidence
              instead of focusing on patient care.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-5">
            {[
              {
                icon: Layers,
                title: "Disconnected systems",
                body: "Policies, incidents, audits and credentials scattered across tools.",
              },
              {
                icon: Users,
                title: "Manual & reactive",
                body: "Clinicians chase paperwork instead of focusing on care.",
              },
              {
                icon: ShieldCheck,
                title: "Compliance under pressure",
                body: "Fragmented governance means audit stress and avoidable risk.",
              },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={i}
                  className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`challenge-card-${i}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-[#1F5D8C]/10 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#1F5D8C]" />
                  </div>
                  <div className="font-heading font-semibold text-slate-900 mb-2">
                    {c.title}
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {c.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section className="py-20 md:py-28 bg-white border-y border-slate-200" data-testid="modules-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#22A34A] mb-4">
              The Solution — Clinical Governance in a Box
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight leading-tight">
              One platform. Every module. Fully integrated.
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              QC4Health digitises and connects your clinical governance
              lifecycle out of the box — so you can operate with clarity,
              consistency and confidence.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              return (
                <div
                  key={m.slug}
                  className="group bg-white rounded-2xl p-7 border border-slate-100 hover:border-[#22A34A]/30 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all"
                  data-testid={`home-module-card-${m.slug}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#22A34A]/10 flex items-center justify-center mb-5 group-hover:bg-[#22A34A] transition-colors">
                    <Icon className="w-5 h-5 text-[#22A34A] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-1">
                    {m.title}
                  </h3>
                  <p className="text-sm text-[#1F5D8C] font-medium mb-3">
                    {m.tagline}
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {m.description}
                  </p>
                </div>
              );
            })}

            {/* AI Card - featured */}
            <div
              className="relative overflow-hidden rounded-2xl p-7 bg-gradient-to-br from-[#0F172A] to-[#1F5D8C] text-white shadow-lg"
              data-testid="home-module-card-ai"
            >
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#3FB8E5]/30 blur-3xl" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center mb-5">
                  <Sparkles className="w-5 h-5 text-[#3FB8E5]" />
                </div>
                <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#3FB8E5] mb-2">
                  Woven Throughout
                </div>
                <h3 className="font-heading text-xl font-semibold mb-1">
                  Embedded AI
                </h3>
                <p className="text-sm text-white/70 leading-relaxed">
                  {AI_MODULE.description}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/product"
              data-testid="home-explore-modules-link"
              className="inline-flex items-center gap-2 text-[#22A34A] font-medium hover:gap-3 transition-all"
            >
              Explore every module
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 md:py-28" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-5">
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#22A34A] mb-4">
                Outcomes
              </div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight leading-tight">
                Smarter governance. Safer care. Less effort.
              </h2>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                Real-time visibility across every ward, unit and service.
                Empower teams, boost front-line capacity, and reduce
                compliance overhead with measurable outcomes.
              </p>
              <Link
                to="/book-demo"
                data-testid="benefits-book-demo-btn"
                className="mt-8 inline-flex items-center gap-2 bg-[#22A34A] hover:bg-[#1e9042] text-white rounded-full px-7 py-3 font-medium transition-all hover:-translate-y-0.5"
              >
                See it in action
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
              {[
                {
                  icon: Users,
                  title: "Empowered teams",
                  body: "Standardised risk language across every ward. Higher-quality assurance data. Accountability at every level.",
                },
                {
                  icon: Zap,
                  title: "Front-line capacity",
                  body: "Up to 60% less time capturing assurance data. More capacity for meaningful, patient-facing work.",
                },
                {
                  icon: BarChart3,
                  title: "Consistent, quality care",
                  body: "Real-time alerts and clear accountability. Faster response times across clinical workflows.",
                },
                {
                  icon: ShieldCheck,
                  title: "Reduced overhead",
                  body: "30% reduction in audit resource. 40% less compliance workload. Instant access to evidence.",
                },
              ].map((b, i) => {
                const Icon = b.icon;
                return (
                  <div
                    key={i}
                    className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm"
                    data-testid={`benefit-card-${i}`}
                  >
                    <div className="w-11 h-11 rounded-xl bg-[#3FB8E5]/15 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-[#1F5D8C]" />
                    </div>
                    <div className="font-heading font-semibold text-slate-900 mb-2">
                      {b.title}
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {b.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* STANDARDS */}
      <section className="py-14 bg-white border-y border-slate-200" data-testid="standards-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#22A34A] mb-2">
                Accreditation-Ready
              </div>
              <div className="font-heading text-xl md:text-2xl font-semibold text-slate-900">
                Accelerated reporting for the standards that matter.
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {STANDARDS.map((s, i) => (
                <span
                  key={i}
                  className="px-5 py-2.5 rounded-full bg-slate-50 border border-slate-200 text-sm font-medium text-slate-700"
                  data-testid={`standard-badge-${i}`}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="py-20 md:py-28" data-testid="cta-banner">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-black text-white p-10 md:p-16">
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#22A34A]/25 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#3FB8E5]/20 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#3FB8E5] mb-4">
                  Book a Demo
                </div>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
                  See QC4Health in your hospital — in 60 minutes.
                </h2>
                <p className="mt-5 text-white/70 max-w-lg leading-relaxed">
                  Walk through the modules, see the AI in action, and get
                  answers tailored to your governance program.
                </p>
              </div>
              <div className="flex md:justify-end">
                <Link
                  to="/book-demo"
                  data-testid="cta-banner-book-demo-btn"
                  className="inline-flex items-center gap-2 bg-[#22A34A] hover:bg-[#1e9042] text-white rounded-full px-8 py-4 font-medium transition-all hover:-translate-y-0.5 shadow-[0_10px_28px_-8px_rgba(34,163,74,0.55)]"
                >
                  Book your 60-minute demo
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
